import {
  Activity,
  BadgeDollarSign,
  Bell,
  BookOpen,
  BrainCircuit,
  BriefcaseBusiness,
  CalendarCheck2,
  ChevronDown,
  Clapperboard,
  Command,
  Compass,
  GraduationCap,
  Languages,
  LayoutDashboard,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  RotateCcw,
  Settings,
  Sparkles,
  Target,
  X
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { formatDateTime } from "../lib/format";
import { Button, LoadingView } from "./ui";
import { GlobalModals } from "./modals/GlobalModals";
import "../workbench-host.css";

const primaryNav = [
  { to: "/today", label: "今日", icon: LayoutDashboard },
  { to: "/timeline", label: "时间轴", icon: Activity },
  { to: "/projects", label: "目标与项目", icon: Target }
];

const secondaryNav = [
  { to: "/work", label: "工作系统", icon: BriefcaseBusiness },
  { to: "/learning", label: "技能与兴趣", icon: BookOpen },
  { to: "/plans", label: "学习计划", icon: GraduationCap },
  { to: "/english", label: "英语学习", icon: Languages },
  { to: "/works", label: "作品集", icon: Clapperboard },
  { to: "/growth", label: "人物面板", icon: Sparkles },
  { to: "/finance", label: "财务与资产", icon: BadgeDollarSign },
  { to: "/reviews", label: "复盘中心", icon: CalendarCheck2 },
  { to: "/ai", label: "帝王引擎", icon: BrainCircuit }
];

const mobileNav = [
  { to: "/today", label: "今日", icon: LayoutDashboard },
  { to: "/timeline", label: "时间轴", icon: Activity },
  { to: "/projects", label: "项目", icon: Target }
];

export function AppShell({ children }: { children: ReactNode }) {
  const { data, dataRevision, loading, error, openModal, toast, refresh, notify } = useApp();
  const [mobileMore, setMobileMore] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("eo_sidebar_collapsed") === "1");
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("eo_sidebar_collapsed", next ? "1" : "0");
      return next;
    });
  };
  const location = useLocation();
  const personalMainlineLight = ["/today", "/timeline", "/projects", "/work", "/learning"].includes(location.pathname);

  useEffect(() => {
    setMobileMore(false);
    setNavOpen(false);
  }, [location.pathname]);

  const todayLabel = useMemo(() => {
    const options: Intl.DateTimeFormatOptions = {
        month: "long",
        day: "numeric",
        weekday: "long"
    };
    try {
      return new Intl.DateTimeFormat("zh-CN", { ...options, timeZone: data?.profile?.timezone || "Asia/Shanghai" }).format(new Date());
    } catch {
      return new Intl.DateTimeFormat("zh-CN", { ...options, timeZone: "Asia/Shanghai" }).format(new Date());
    }
  }, [dataRevision, data?.profile?.timezone]);

  if (loading) return <LoadingView />;

  return (
    <div className={`app-shell ${sidebarCollapsed ? "app-shell--collapsed" : ""}`}>
      <aside className={`sidebar ${navOpen ? "sidebar--open" : ""} ${sidebarCollapsed ? "sidebar--hidden" : ""}`}>
        <div className="brand">
          <div className="brand__mark">
            <Compass size={22} strokeWidth={2.3} />
          </div>
          <div>
            <strong>地球 Online</strong>
            <span>个人成长操作系统</span>
          </div>
          <button className="sidebar__close" onClick={() => setNavOpen(false)} aria-label="关闭导航">
            <X size={19} />
          </button>
        </div>

        <div className="world-switcher">
          <div className="world-switcher__avatar">
            {(data?.profile?.display_name?.slice(0, 1) || "我").toUpperCase()}
          </div>
          <div>
            <strong>{data?.profile?.display_name || "我的世界"}</strong>
            <span>仅自己可见</span>
          </div>
          <ChevronDown size={16} />
        </div>

        <nav className="sidebar-nav" aria-label="主导航">
          <div className="sidebar-nav__group">
            <span>个人主线</span>
            {primaryNav.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </div>
          <div className="sidebar-nav__group">
            <span>领域空间</span>
            {secondaryNav.map((item) => (
              <div key={item.to}>
                <NavItem {...item} />
                {item.to === "/work" && (
                  <div className="sidebar-work-children" aria-label="工作系统子栏目">
                    <NavLink to="/work/desk">业务工作台</NavLink>
                    <NavLink to="/work" end>客户与订单</NavLink>
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        <div className="sidebar__footer">
          <button className="sidebar-collapse-btn" onClick={toggleSidebar} aria-label="收起侧边栏">
            <PanelLeftClose size={17} />
            <span>收起侧边栏</span>
          </button>
          <NavLink className="sidebar-link" to="/settings">
            <Settings size={18} />
            <span>设置与审计</span>
          </NavLink>
          <div className="privacy-badge">
            <span className="privacy-badge__dot" />
            {data?.meta.ownerMode === "BROWSER_TRIAL" ? "浏览器试用空间" : "本地私密空间"}
          </div>
        </div>
      </aside>
      <button
        className={`sidebar-scrim ${navOpen ? "sidebar-scrim--show" : ""}`}
        onClick={() => setNavOpen(false)}
        aria-label="关闭导航"
      />

      <div className={`main-frame ${personalMainlineLight ? "main-frame--personal-light" : ""}`}>
        <header className="topbar">
          <div className="topbar__left">
            <button className="mobile-menu" onClick={() => setNavOpen(true)} aria-label="打开导航">
              <Menu size={20} />
            </button>
            <button className="sidebar-expand-btn" onClick={toggleSidebar} aria-label="展开侧边栏">
              <PanelLeftOpen size={20} />
            </button>
            <button className="global-search" onClick={() => openModal("search")}>
              <Search size={17} />
              <span>搜索任务、项目、记录…</span>
              <kbd>
                <Command size={12} /> K
              </kbd>
            </button>
          </div>
          <div className="topbar__date">
            <span>{todayLabel}</span>
            {data?.meta.syncedAt && <small>更新于 {formatDateTime(data.meta.syncedAt)}</small>}
          </div>
          <div className="topbar__actions">
            <button className="icon-button" aria-label="通知" onClick={() => notify("当前没有新的系统通知", "info")}>
              <Bell size={19} />
              <i />
            </button>
            <Button icon={<Plus size={17} />} onClick={() => openModal("quick")}>
              新增事项
            </Button>
          </div>
        </header>

        {data?.meta.ownerMode === "BROWSER_TRIAL" && (
          <div className="trial-banner">
            <span>云端试用版</span>
            当前数据只保存在这个浏览器中；清除浏览器数据或更换设备后不会自动同步。
          </div>
        )}

        {error && (
          <div className="connection-banner">
            <span>{error}</span>
            <Button variant="ghost" size="sm" onClick={() => void refresh()} icon={<RotateCcw size={14} />}>
              重新连接
            </Button>
          </div>
        )}

        <main className="content">{data ? children : null}</main>
      </div>

      <nav className="mobile-nav" aria-label="移动端导航">
        {mobileNav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? "active" : "")}>
              <Icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
        <button className="mobile-nav__create" onClick={() => openModal("quick")}>
          <span>
            <Plus size={22} />
          </span>
          <small>新增</small>
        </button>
        <button className={mobileMore ? "active" : ""} onClick={() => setMobileMore((open) => !open)}>
          <Menu size={20} />
          <span>更多</span>
        </button>
      </nav>

      {mobileMore && (
        <div className="mobile-more">
          <div className="mobile-more__handle" />
          <div className="mobile-more__grid">
            {[...secondaryNav, { to: "/work/desk", label: "业务工作台", icon: BriefcaseBusiness }, { to: "/settings", label: "设置与审计", icon: Settings }].map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.to} to={item.to}>
                  <span><Icon size={20} /></span>
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast toast--${toast.tone}`} role="status">
          <span>{toast.message}</span>
        </div>
      )}
      <GlobalModals />
    </div>
  );
}

function NavItem({
  to,
  label,
  icon: Icon
}: {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}) {
  return (
    <NavLink className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} to={to}>
      <Icon size={18} />
      <span>{label}</span>
    </NavLink>
  );
}
